<script setup lang="ts">
import type { SignInError } from '@/lib/signInError'
import { computed, ref } from 'vue'
import { submitChallenge } from '@/lib/challenge'

const props = withDefaults(defineProps<{
  returnUrl: string | null
  challengeEndpoint: string
  signInError?: SignInError | null
  forgotPasswordUrl?: string | null
  navigate?: (url: string) => void
}>(), {
  signInError: null,
  forgotPasswordUrl: null,
  navigate: (url: string) => { window.location.assign(url) },
})

// One deny for every failing address (VORG-1190 L2).
const GENERIC_ERROR = "We couldn't sign you in. Check the address and try again."

// The authority redirected a failed sign-in here.
const SIGN_IN_ERRORS: Record<SignInError, string> = {
  signin_failed: "We couldn't finish signing you in. Please try again.",
}

const email = ref('')
const busy = ref(false)
const error = ref<string | null>(null)
const notice = ref(props.signInError ? SIGN_IN_ERRORS[props.signInError] : null)
const message = computed(() => error.value ?? notice.value)

async function onSubmit() {
  if (busy.value) {
    return
  }
  notice.value = null
  const address = email.value.trim()
  if (address === '') {
    error.value = GENERIC_ERROR
    return
  }

  error.value = null
  busy.value = true

  const result = await submitChallenge({
    email: address,
    returnUrl: props.returnUrl,
    endpoint: props.challengeEndpoint,
  })

  if (result.kind === 'redirect') {
    props.navigate(result.redirectUrl)
    return
  }

  error.value = GENERIC_ERROR
  busy.value = false
}
</script>

<template>
  <form
    data-testid="login-form"
    class="login-form"
    novalidate
    @submit.prevent="onSubmit"
  >
    <label
      class="sr-only"
      for="login-email"
    >Email Address</label>
    <!-- type=text, no pattern: a validity gate is an oracle. -->
    <input
      id="login-email"
      v-model="email"
      data-testid="email"
      class="login-form__input"
      type="text"
      inputmode="email"
      placeholder="Email Address"
      autocomplete="username"
      autocapitalize="off"
      spellcheck="false"
      :aria-invalid="error !== null"
      aria-describedby="login-error"
    >

    <p
      v-if="message"
      id="login-error"
      data-testid="error"
      class="login-form__error"
      role="alert"
    >
      {{ message }}
    </p>

    <div class="login-form__actions">
      <button
        data-testid="submit"
        class="login-form__submit"
        type="submit"
        :disabled="busy"
      >
        {{ busy ? 'Logging in…' : 'Log in' }}
      </button>

      <a
        v-if="forgotPasswordUrl"
        data-testid="forgot-password"
        class="login-form__link"
        :href="forgotPasswordUrl"
      >Forgot your password?</a>
    </div>
  </form>
</template>
